import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Button, Alert, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Card, CardContent, Divider, Collapse,
  IconButton, Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import billingApi from '../../services/billingApi';

const STATUS_COLORS = { draft: 'warning', active: 'success', archived: 'default' };

import { formatDate } from '../../utils/formatters';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

function formatFeatureValue(value) {
  if (value === -1) return 'Unlimited';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export default function PricingHistory() {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedConfig, setExpandedConfig] = useState(null);
  const [configDetail, setConfigDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareDialog, setCompareDialog] = useState(false);
  const [compareIds, setCompareIds] = useState({ a: '', b: '' });
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    try {
      const data = await billingApi.get('/api/admin/products');
      setProducts(data.products || []);
      if (data.products?.length > 0) {
        const configured = data.products.find(p => p.api_url) || data.products[0];
        setSelectedProductId(configured.id);
        await loadConfigs(configured.id);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadConfigs(productId) {
    try {
      const data = await billingApi.get(`/api/admin/pricing-configs?product_id=${productId}`);
      setConfigs(data.configs || []);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleProductChange(productId) {
    setSelectedProductId(productId);
    setExpandedConfig(null);
    setConfigDetail(null);
    await loadConfigs(productId);
  }

  async function handleExpandConfig(configId) {
    if (expandedConfig === configId) {
      setExpandedConfig(null);
      setConfigDetail(null);
      return;
    }

    setExpandedConfig(configId);
    setLoadingDetail(true);
    try {
      const data = await billingApi.get(`/api/admin/pricing-configs/${configId}`);
      setConfigDetail(data.config);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleCompare() {
    if (!compareIds.a || !compareIds.b) return;
    try {
      const data = await billingApi.get(`/api/admin/pricing-configs/compare?a=${compareIds.a}&b=${compareIds.b}`);
      setDiff(data.diff);
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) {
    return <AdminPageSkeleton />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">Pricing History</Typography>
        <Button variant="outlined" onClick={() => setCompareDialog(true)} disabled={configs.length < 2} size="small">
          Compare Versions
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <FormControl sx={{ mb: 3, minWidth: 300 }} size="small">
        <InputLabel>Product</InputLabel>
        <Select value={selectedProductId} onChange={(e) => handleProductChange(e.target.value)} label="Product">
          {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </Select>
      </FormControl>

      {/* Config Timeline */}
      {configs.map(config => (
        <Card key={config.id} variant="outlined" sx={{ mb: 1.5, overflow: 'visible' }}>
          {/* Config Header Row — clickable to expand */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
              cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' }
            }}
            onClick={() => handleExpandConfig(config.id)}
          >
            <IconButton size="small" aria-label="Expand pricing config">
              {expandedConfig === config.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {config.version_label || config.id.substring(0, 8)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }}>
                {config.id}
              </Typography>
            </Box>

            <Chip label={config.status} color={STATUS_COLORS[config.status]} size="small" />

            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {config.plan_count} plan{config.plan_count !== 1 ? 's' : ''}
              </Typography>
              {config.effective_at && (
                <Typography variant="caption" color="text.secondary">
                  Effective: {formatDate(config.effective_at)}
                </Typography>
              )}
            </Box>

            {config.retired_at && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">
                  Retired: {formatDate(config.retired_at)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Expanded Detail */}
          <Collapse in={expandedConfig === config.id}>
            <Divider />
            <Box sx={{ p: 2 }}>
              {config.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {config.notes}
                </Typography>
              )}

              {loadingDetail ? (
                <AdminPageSkeleton bare rows={5} />
              ) : configDetail ? (
                <Grid container spacing={2}>
                  {(configDetail.plans || []).map(plan => (
                    <Grid item xs={12} md={6} lg={4} key={plan.id}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="h6">
                              {plan.name}
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight="bold">
                              ${(plan.price_cents / 100).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <Chip label={plan.interval_type} size="small" variant="outlined" />
                            <Chip label={plan.planId} size="small" variant="outlined" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace" }} />
                            {plan.trial_days > 0 && (
                              <Chip label={`${plan.trial_days}d trial`} size="small" color="success" variant="outlined" />
                            )}
                          </Box>

                          {plan.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                              {plan.description}
                            </Typography>
                          )}

                          <Divider sx={{ my: 1 }} />

                          {/* Features */}
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' }}>
                            Features
                          </Typography>

                          {Object.keys(plan.features || {}).length > 0 ? (
                            <Table size="small" sx={{ mt: 0.5 }}>
                              <TableBody>
                                {Object.entries(plan.features || {}).map(([key, value]) => (
                                  <TableRow key={key} sx={{ '& td': { py: 0.25, px: 0.5, border: 'none' } }}>
                                    <TableCell>
                                      <Typography variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", color: 'text.secondary' }}>
                                        {key}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="caption" fontWeight="bold">
                                        {formatFeatureValue(value)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                              Fully unrestricted
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">No detail available</Typography>
              )}
            </Box>
          </Collapse>
        </Card>
      ))}

      {configs.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No pricing configurations found for this product</Typography>
        </Paper>
      )}

      {/* Compare Dialog */}
      <Dialog open={compareDialog} onClose={() => { setCompareDialog(false); setDiff(null); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontFamily: "'EB Garamond', serif" }}>Compare Configurations</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Config A</InputLabel>
              <Select value={compareIds.a} onChange={(e) => setCompareIds(ids => ({ ...ids, a: e.target.value }))} label="Config A">
                {configs.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.version_label || c.id.substring(0, 8)} ({c.status})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Config B</InputLabel>
              <Select value={compareIds.b} onChange={(e) => setCompareIds(ids => ({ ...ids, b: e.target.value }))} label="Config B">
                {configs.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.version_label || c.id.substring(0, 8)} ({c.status})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleCompare} disabled={!compareIds.a || !compareIds.b} size="small">
              Compare
            </Button>
          </Box>

          {diff && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Change</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {diff.plan_diffs.map((d, i) => (
                    <TableRow key={i}>
                      <TableCell><strong>{d.planId}</strong></TableCell>
                      <TableCell>
                        <Chip
                          label={d.status.replace('_', ' ')}
                          color={d.status === 'added_in_b' ? 'success' : d.status === 'removed_in_b' ? 'error' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {d.price_change && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Price: <strong>${(d.price_change.a / 100).toFixed(2)}</strong> → <strong>${(d.price_change.b / 100).toFixed(2)}</strong>
                          </Typography>
                        )}
                        {d.feature_changes?.map((fc, j) => (
                          <Typography key={j} variant="body2" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.75rem' }}>
                            {fc.feature_key}: <span style={{ color: '#8B2635' }}>{formatFeatureValue(fc.value_a)}</span> → <span style={{ color: '#2D6A4F' }}>{formatFeatureValue(fc.value_b)}</span>
                          </Typography>
                        ))}
                        {!d.price_change && (!d.feature_changes || d.feature_changes.length === 0) && (
                          <Typography variant="caption" color="text.secondary">No changes</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {diff.plan_diffs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">No differences found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCompareDialog(false); setDiff(null); }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
